/* SUNDAY BATCH · D-4 — the risk form's FS head picker. Stores the picked heads as structured codes on the risk
   (``fs_codes: ["4101", …]``); the heat map / scoping read only that link (backend scoping_worksheet._risk_fs_codes).
   The list is the FS taxonomy (GET /fs/taxonomy: the master heads + this firm's custom codes); the server refuses an unknown
   code (RISK_FS_CODE_UNKNOWN). No pick = "not linked" (shown as such — never guessed from the FS area).
   ``value``: the stored fs_codes (an array), or null for a risk saved before D-4 — the picker then shows ``fallback`` (the heads
   the register row counts under today, fs_heads) and sends nothing until the auditor changes it (the edit keeps the row as is). */
import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import api from "@/lib/api";
import { asArray } from "@/lib/asArray";
import { Label } from "@/components/ui/label";

let _cache = null;    // the taxonomy list, loaded once per page load

export default function RiskFsHeadPicker({ value, fallback, onChange, t, lang, testidPrefix = "risk-fs" }) {
  const [heads, setHeads] = useState(_cache || []);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (_cache) return;
    let cancel = false;
    api.get("/fs/taxonomy", { _silent: true })
      .then((r) => { _cache = asArray(r?.data?.fs_lines); if (!cancel) setHeads(_cache); })
      .catch(() => { if (!cancel) setFailed(true); });
    return () => { cancel = true; };
  }, []);
  const codes = Array.isArray(value) ? value : asArray(fallback);
  const byCode = Object.fromEntries(heads.map((h) => [h.code, h]));
  const name = (c) => {
    const h = byCode[c];
    return h ? (lang === "ar" && h.line_ar ? h.line_ar : h.line) : "";
  };
  const add = (c) => { if (c && !codes.includes(c)) onChange([...codes, c]); };
  const remove = (c) => onChange(codes.filter((x) => x !== c));
  return (
    <div data-testid={`${testidPrefix}-heads`}>
      <Label>{t("sbD.fsHeads")}</Label>
      <p className="text-xs text-muted-foreground">{t("sbD.fsHeadsHint")}</p>
      <div className="mt-1 flex flex-wrap gap-1">
        {codes.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 rounded border bg-slate-50 px-2 py-0.5 text-xs dark:bg-slate-900"
                data-testid={`${testidPrefix}-head-${c}`}>
            {c}{name(c) ? ` · ${name(c)}` : ""}
            <button type="button" onClick={() => remove(c)} aria-label={t("sbD.removeHead").replace("{code}", c)}
                    data-testid={`${testidPrefix}-head-remove-${c}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
        {codes.length === 0 && (
          <span className="text-xs text-amber-700 dark:text-amber-300" data-testid={`${testidPrefix}-not-linked`}>{t("sbD.notLinked")}</span>
        )}
      </div>
      <select className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm" value=""
              onChange={(e) => add(e.target.value)} data-testid={`${testidPrefix}-head-add`}>
        <option value="">{t("sbD.addHead")}</option>
        {heads.filter((h) => !codes.includes(h.code)).map((h) => (
          <option key={h.code} value={h.code}>{h.code} · {lang === "ar" && h.line_ar ? h.line_ar : h.line}</option>
        ))}
      </select>
      {failed && <p className="mt-1 text-xs text-red-700" data-testid={`${testidPrefix}-head-failed`}>{t("sbD.loadFailed")}</p>}
    </div>
  );
}
